/**
 * SkillPickerKeyboard hook for keyboard navigation in the skills picker popup.
 *
 * Handles:
 * - Arrow key navigation (up/down)
 * - Enter to select current skill
 * - Escape to close picker
 * - Communication back to extension via chrome.runtime.sendMessage
 *
 * Data attributes:
 * - data-extension-id: Chrome extension ID for targeted messaging
 */

import { safePushEvent } from '../lib/safe_push.js';

// Message types matching WBEvents.js in extension
const BB_SKILL_SELECTED = 'BB_SKILL_SELECTED';
const BB_SKILL_PICKER_CANCEL = 'BB_SKILL_PICKER_CANCEL';

export default {
  mounted() {
    this.extensionId = this.el.dataset.extensionId;

    // Store the bound function references for proper cleanup
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleBlur = this.handleWindowBlur.bind(this);

    // Focus the search input on mount
    const searchInput = document.getElementById('skill-search-input');
    if (searchInput) {
      searchInput.focus();
      searchInput.addEventListener('keydown', this.boundHandleKeydown);
    }

    // Close picker when window loses focus
    window.addEventListener('blur', this.boundHandleBlur);

    // Listen for skill selection to notify extension
    // Extension handles closing the popup window
    this.handleEvent('skill_selected', (data) => {
      this.sendToExtension({ type: BB_SKILL_SELECTED, skill: data.skill });
    });

    // Listen for close event
    this.handleEvent('close_picker', () => {
      this.sendToExtension({ type: BB_SKILL_PICKER_CANCEL });
    });
  },

  /**
   * Send message to Wavebox extension via chrome.runtime.sendMessage
   */
  sendToExtension(message) {
    chrome.runtime.sendMessage(this.extensionId, message);
  },

  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        safePushEvent(this,'navigate', { direction: 'down' });
        this.scrollToSelected();
        break;

      case 'ArrowUp':
        e.preventDefault();
        safePushEvent(this,'navigate', { direction: 'up' });
        this.scrollToSelected();
        break;

      case 'Enter':
        e.preventDefault();
        safePushEvent(this,'select_current', {});
        break;

      case 'Escape':
        e.preventDefault();
        safePushEvent(this,'close', {});
        break;
    }
  },

  handleWindowBlur() {
    safePushEvent(this,'close', {});
  },

  scrollToSelected() {
    // Small delay to let the server update the selected index
    setTimeout(() => {
      const selected = document.querySelector('[id^="skill-item-"].bg-primary\\/10');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 50);
  },

  destroyed() {
    const searchInput = document.getElementById('skill-search-input');
    if (searchInput && this.boundHandleKeydown) {
      searchInput.removeEventListener('keydown', this.boundHandleKeydown);
    }
    if (this.boundHandleBlur) {
      window.removeEventListener('blur', this.boundHandleBlur);
    }
  }
};
